import { useEffect, useRef } from 'react';
import { accountSchema } from '@forecastify/shared';
import { DEFAULT_THEME, SYSTEM_THEME, applyTheme, isDarkTheme } from '@forecastify/ui';
import { rememberLocale, useI18n } from './i18n';
import { useSession } from './session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE STORED THEME, APPLIED. Nothing did this before.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The theme has been a member preference since C1 — a column on `members`, a field on the
 * preferences contract, a gallery in account settings that calls `applyTheme` when you pick one.
 * **And that was the only thing that ever called it.** Choosing Mocha painted Mocha, and the next
 * reload came back light, because no code between sign-in and the first frame read the stored
 * value. The setting persisted perfectly and did nothing.
 *
 * ⚠️ **The server is the source of truth, and `localStorage` is only a PAINT HINT.** The
 * specification is explicit that the theme follows the person across devices, which is why it is
 * not a browser setting. But a value that only exists on the server cannot be known before the
 * first round trip, and a dark-theme user must never see a white flash — so the resolved id is
 * cached locally, `index.html` reads it in a blocking script before the first paint, and the
 * fetched value corrects it a moment later. A new device paints the default once and is then
 * right forever after; the cache is never read as the answer, only as the guess.
 */

/** ⚠️ Spelled the same in `index.html`'s inline script. Two literals, one meaning. */
export const THEME_HINT_KEY = 'forecastify.theme';

/**
 * Record the resolved choice for the next cold start.
 *
 * ⚠️ **Wrapped, because storage throws rather than returning null in a locked-down browser.**
 * Safari's private mode and any profile with site data blocked raise on `setItem`, and a theme
 * preference is not worth taking the application down for.
 */
export function rememberTheme(id: string): void {
  try {
    globalThis.localStorage.setItem(THEME_HINT_KEY, id);
  } catch {
    /* No cache, so the next cold start paints the default first. That is the whole cost. */
  }
}

/**
 * Apply the signed-in member's stored preferences: their theme, and their language.
 *
 * ⚠️ **Mounted inside the session provider and above the router**, so it runs once per sign-in
 * rather than once per navigation — and so switching theme never remounts a screen or loses view
 * state, which the specification asks for by name. Nothing here renders.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE LANGUAGE JOINED THE THEME HERE, AND IT JOINED IT FOR THE EXACT   │
 * │ REASON THE HEADER ABOVE DESCRIBES.                                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"The setting persisted perfectly and did nothing"* was written about the theme in C21 and was
 * equally true of `user_preferences.locale` until now: the account screen wrote it, the API read it
 * for mail and for the Agent's replies, and nothing between sign-in and the first frame read it —
 * so the **interface** followed `navigator.languages` while the **Agent** followed the setting. It
 * surfaced as *"the Agent does not respect the language Forecastify is set to"*, and the Agent was
 * the half that was right.
 *
 * ⚠️⚠️ **ONE FETCH FOR BOTH, WHICH IS NOT TIDINESS.** This file's own scar is *"ten `GET /account`
 * requests on one sign-in, each aborting the one before it, none of them finishing"* — a second
 * boot component beside this one would be a second request per sign-in for a second field of the
 * same row. So the language is applied where the response already is, and this component was
 * renamed from `ThemeBoot` because a component that applies two preferences should not be named
 * after one of them.
 */
export function PreferencesBoot(): null {
  const session = useSession();
  /**
   * ⚠️ **The setter, not the current value.** Reading `locale` here would put this component's
   * effect in the path of every language change and make the dependency list argue with the ref
   * below; what it needs is the ability to move the app, once, to what the server holds.
   */
  const { setLocale } = useI18n();
  const status = session.status;
  const workspaceId = session.workspaceId;

  /**
   * ⚠⚠️ **THE SESSION GOES IN A REF, AND THE EFFECT DEPENDS ON `status` ALONE.**
   *
   * `useSession()` returns a fresh object every render — it is a context value built inline — so
   * listing it in the dependency array re-runs the effect on every render of the whole tree. The
   * first version did, and the cost was visible immediately in the browser: **ten `GET /account`
   * requests on one sign-in**, each aborting the one before it, none of them finishing, and the
   * theme never applied. The last one made the API answer 500.
   *
   * The ref is read inside the effect and never in the dependency array, which is the point: the
   * effect always sees the current session and only re-runs when the thing it actually cares
   * about — whether somebody is signed in — changes.
   */
  const sessionRef = useRef(session);
  sessionRef.current = session;

  useEffect(() => {
    /* ⚠⚠️ **`workspaceId` TOO, AND NOT BECAUSE THE THEME IS WORKSPACE-SCOPED — IT IS NOT.**
     * `/account` is a guarded route like every other, so it needs `x-workspace-id`, and `status`
     * becomes `signed-in` a beat before the workspace is chosen. Firing on `status` alone sent
     * the request without the header and the API answered **500**, which the browser showed as
     * a theme that simply never applied. `account-route.tsx` records the same rule for the same
     * reason: *"No workspace means no request."* */
    if (status !== 'signed-in' || workspaceId === null) return;

    /* ⚠️ An `AbortController` rather than a captured `let`. The obvious `let cancelled = false`
     * does not typecheck: nothing inside the closure writes it, so it narrows to `false` and both
     * guards are dead code. A signal is the honest object anyway — the question is "has this
     * effect been torn down", not "did a local change".
     *
     * ⚠️ **Checked ONCE, after the last `await`.** `aborted` is a readonly boolean, so a first
     * check narrows it to `false` for the rest of the function and a second read is dead code. */
    const abort = new AbortController();

    void (async () => {
      const response = await sessionRef.current.request('/account').catch(() => null);
      if (response?.ok !== true) return;

      /* Parsed with the shared schema rather than cast: a `theme` that came back as a number
       * would otherwise reach `applyTheme` and set `data-theme="3"`. */
      const parsed = accountSchema.safeParse(await response.json().catch(() => null));
      if (!parsed.success || abort.signal.aborted) return;

      applyTheme(parsed.data.preferences.theme);
      rememberTheme(parsed.data.preferences.theme);

      /**
       * ⚠️⚠️ **THE SERVER'S ANSWER WINS OVER THE BROWSER'S GUESS, AND THAT IS WHAT A SETTING
       * MEANS.** `preferredLocale` guessed from `navigator.languages` to get the first frame drawn;
       * this is the correction, and from here on the two halves of the product agree — the screens
       * and the Agent read one fact.
       *
       * ⚠️ **A consequence worth naming**: `user_preferences.locale` is `not null default 'hu'`
       * (`0010`), so an account that never opened the language dropdown says Hungarian — and an
       * English-browser member who had an English interface by accident now gets the Hungarian one
       * the setting asks for, until they set English. That is the correct behaviour of a setting and
       * it is a visible change; `decisions.md` R-6 is the row that makes Hungarian the default
       * rather than the fallback.
       *
       * ⚠️ **Remembered even when it matches**, so a cold start on this device skips the wrong
       * first frame entirely rather than only when the value has just changed.
       */
      setLocale(parsed.data.preferences.locale);
      rememberLocale(parsed.data.preferences.locale);
    })();

    return () => {
      abort.abort();
    };
    /* ⚠️ `setLocale` is in the list because the lint rule requires it and it is stable in practice —
     * `app.tsx` passes a `useState` setter, and the provider memoises the context on it. A version
     * that recreated it every render would re-run this effect, which is the ten-requests bug above
     * in a new costume; the ref pattern is deliberately NOT used for it, because a stale setter
     * would move the app to a locale nobody chose. */
  }, [status, workspaceId, setLocale]);

  /**
   * ⚠️ **`system` has to keep listening, because the device can change while the tab is open.**
   * A laptop that switches to dark at sunset, or a person toggling appearance in system settings,
   * changes what `system` resolves to — and a theme that only resolves at boot is stuck light
   * until the next reload. The listener is attached unconditionally and re-reads the stored hint
   * each time, so it is correct regardless of what the fetch above lands on.
   */
  useEffect(() => {
    if (typeof globalThis.matchMedia !== 'function') return;
    const query = globalThis.matchMedia('(prefers-color-scheme: dark)');

    const follow = (): void => {
      let stored: string | null = null;
      try {
        stored = globalThis.localStorage.getItem(THEME_HINT_KEY);
      } catch {
        stored = null;
      }
      if (stored !== SYSTEM_THEME) return;
      applyTheme(SYSTEM_THEME);
    };

    query.addEventListener('change', follow);
    return () => {
      query.removeEventListener('change', follow);
    };
  }, []);

  return null;
}

/**
 * What the inline script in `index.html` does, expressed once so a test can check the two agree.
 *
 * ⚠️ **Exported for `__tests__/preferences-boot.test.tsx` rather than for production**, which imports
 * nothing before the first paint by definition. The test renders the same logic against a fake
 * storage and asserts the attribute, so a change to `applyTheme`'s contract cannot silently leave
 * the inline script painting the wrong palette.
 */
export function paintStoredTheme(stored: string | null, root: HTMLElement): void {
  /* ⚠️ Forecastify Light when nothing is cached — the same default the column carries and
   * the same one `index.html` paints. Three places, one answer. */
  const id = stored ?? DEFAULT_THEME;
  applyTheme(id, root);
  /* A defensive positive: `applyTheme` resolves `system` itself, and this asserts nothing — it is
   * here so the reader can see that the hint is never written to the DOM verbatim. */
  void isDarkTheme(id);
}
